import { useEffect, useState, useMemo, useRef } from "react";
import { useRole } from "@/contexts/RoleContext";
import {
  listTeacherTestResults,
  listDeletedTests,
  getTestResultDetail,
  awardTitaMarks,
  publishTestScores,
  bulkSoftDeleteTests,
  restoreTest,
  hardDeleteTest,
} from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ClipboardList,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  Star,
  Loader2,
  Users,
  Send,
  PenLine,
  TrendingUp,
  Filter,
  ArrowUpDown,
  AlertCircle,
  BarChart3,
  ArrowLeft,
  Layers,
  BookOpen,
  UserCheck,
  Trash2,
  X,
  CheckSquare,
  RotateCcw,
  Flame,
} from "lucide-react";
import { toast } from "sonner";

// ── Chapters display helper ───────────────────────────────────────────────────

function ChaptersLine({ test }) {
  // multi-topic tests store chapters in `topics`; single-topic in `chapter`
  const chapters = Array.isArray(test.topics) && test.topics.length > 0
    ? test.topics
    : test.chapter ? [test.chapter] : [];

  if (chapters.length === 0) return null;

  // 1–2 chapters: plain inline text
  if (chapters.length <= 2) {
    return (
      <p className="text-xs text-slate-500 mt-0.5 truncate" data-testid={`test-chapters-${test.test_id}`}>
        {chapters.join(" · ")}
      </p>
    );
  }

  // 3+ chapters — check if this covers the whole subject curriculum
  const totalChapters = test.total_chapters || 0;
  const isFullCourse = totalChapters > 0 && chapters.length >= totalChapters;

  if (isFullCourse) {
    // Full course: plain text, no hover needed
    return (
      <p className="text-xs text-slate-500 mt-0.5" data-testid={`test-chapters-${test.test_id}`}>
        Full Course · {chapters.length} Chapters
      </p>
    );
  }

  // Partial chapter selection: chip with Radix portal tooltip (never clips)
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 cursor-default mt-0.5"
            data-testid={`test-chapters-${test.test_id}`}
          >
            <BookOpen className="w-3 h-3" /> {chapters.length} Chapters
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs bg-slate-800 text-white border-0 rounded-xl p-2.5">
          <p className="font-semibold mb-1 text-slate-300 text-xs">Chapters</p>
          {chapters.map((c, i) => (
            <p key={i} className="py-0.5 border-b border-slate-700 last:border-0 text-xs">{c}</p>
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Published-to pill helper ──────────────────────────────────────────────────

function PublishedToPill({ test }) {
  const names = test.published_to_names || [];
  const count = test.total_assigned || names.length;

  if (count === 0) return null;

  // Individual test — learner_name already shown in meta; just show "Individual" pill
  if (test.learner_id) {
    return (
      <>
        <span>·</span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700" data-testid={`pub-to-${test.test_id}`}>
          <UserCheck className="w-3 h-3" /> Individual
        </span>
      </>
    );
  }

  // Entire class: plain text with count, no hover needed
  if (test.published_to_all) {
    return (
      <>
        <span>·</span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700" data-testid={`pub-to-${test.test_id}`}>
          <Users className="w-3 h-3" /> Entire Class{count > 0 ? ` · ${count} Students` : ""}
        </span>
      </>
    );
  }

  // Specific student selection: pill with Radix portal tooltip showing names
  const hasNames = names.length > 0;
  const label = `${count} Student${count !== 1 ? "s" : ""}`;

  if (!hasNames) {
    return (
      <>
        <span>·</span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700" data-testid={`pub-to-${test.test_id}`}>
          <Users className="w-3 h-3" /> {label}
        </span>
      </>
    );
  }

  return (
    <>
      <span>·</span>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 cursor-default" data-testid={`pub-to-${test.test_id}`}>
              <Users className="w-3 h-3" /> {label}
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs bg-slate-800 text-white border-0 rounded-xl p-2.5">
            <p className="font-semibold mb-1 text-slate-300 text-xs">Published to</p>
            {names.map((n, i) => (
              <p key={i} className="py-0.5 border-b border-slate-700 last:border-0 text-xs">{n}</p>
            ))}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </>
  );
}

// ── Deleted test card ─────────────────────────────────────────────────────────

function DeletedTestCard({ test, isRestoring, onRestore, onHardDelete }) {
  const chapters = Array.isArray(test.topics) && test.topics.length > 0
    ? test.topics
    : test.chapter ? [test.chapter] : [];

  return (
    <Card
      className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 opacity-80 overflow-hidden"
      data-testid={`deleted-test-card-${test.test_id}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 bg-slate-200 text-slate-400">
              <Trash2 className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-semibold text-slate-600">{test.subject}</p>
                {test.test_type === "FM" && (
                  <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500">FM</span>
                )}
              </div>
              {chapters.length > 0 && (
                <p className="text-xs text-slate-400 mt-0.5 truncate">{chapters.slice(0, 2).join(" · ")}{chapters.length > 2 ? ` +${chapters.length - 2} more` : ""}</p>
              )}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-slate-400">
                <span>Grade {test.grade}</span>
                <span>·</span>
                <span>{test.mode === "manual" ? "Manual" : "Auto"}</span>
                {test.total_questions > 0 && <><span>·</span><span>{test.total_questions} question{test.total_questions !== 1 ? "s" : ""}</span></>}
                {test.total_assigned > 0 && <><span>·</span><span>{test.total_assigned} student{test.total_assigned !== 1 ? "s" : ""} assigned</span></>}
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 text-right space-y-0.5">
            <span className="inline-flex items-center gap-1 text-xs bg-slate-200 text-slate-600 font-semibold px-2 py-0.5 rounded-full">
              <Trash2 className="w-2.5 h-2.5" /> Deleted
            </span>
            {test.deleted_at && (
              <p className="text-xs text-slate-400">{new Date(test.deleted_at).toLocaleDateString()}</p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-200">
          <button
            onClick={onRestore}
            disabled={isRestoring}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition"
            data-testid={`restore-btn-${test.test_id}`}
          >
            {isRestoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Restore
          </button>
          <button
            onClick={onHardDelete}
            disabled={isRestoring}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 transition"
            data-testid={`hard-delete-btn-${test.test_id}`}
          >
            <Flame className="w-3.5 h-3.5" /> Delete Permanently
          </button>
          {test.submissions_count > 0 && (
            <span className="text-xs text-slate-400 ml-auto">{test.submissions_count} submission{test.submissions_count !== 1 ? "s" : ""} preserved</span>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── Submission detail components ──────────────────────────────────────────────

function SubmissionRow({ sub, testAddMarks, testQuestions, onAwardMarks }) {
  const [expanded, setExpanded] = useState(false);
  const [titaMarks, setTitaMarks] = useState({});

  const hasTita = sub.answers?.some((a) => a.answer_type === "TITA");

  const questionMap = Object.fromEntries(
    (testQuestions || []).map((q) => [q.question_number, q])
  );

  const handleSaveTita = async () => {
    try {
      await onAwardMarks(sub.learner_id, titaMarks);
      setTitaMarks({});
      toast.success("Marks saved");
    } catch {
      toast.error("Failed to save marks");
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`submission-row-${sub.learner_id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 transition text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-bold">
            {(sub.learner_name || sub.learner_id).charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-slate-800 text-sm">{sub.learner_name || sub.learner_id}</p>
            <p className="text-xs text-slate-500">
              {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : "—"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {sub.submission_status === "abandoned" ? (
            <span className="text-xs bg-red-100 text-red-700 font-semibold px-2 py-0.5 rounded-full border border-red-200">
              Abandoned: {sub.total_score ?? 0}
            </span>
          ) : sub.scores_released ? (
            <span className="text-xs bg-emerald-100 text-emerald-700 font-semibold px-2 py-0.5 rounded-full border border-emerald-200">
              Released: {sub.total_score !== null ? sub.total_score : "—"}
            </span>
          ) : sub.total_score !== null ? (
            <span className="text-xs bg-sky-100 text-sky-700 font-semibold px-2 py-0.5 rounded-full">
              Ready: {sub.total_score}
            </span>
          ) : (
            <span className="text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full">
              TITA pending
            </span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-3">
          {sub.answers?.map((ans, idx) => {
            const q = questionMap[ans.question_number] || {};
            return (
              <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200 text-sm">
                <p className="font-semibold text-slate-700 mb-1">
                  Q{ans.question_number}. {q.question_text || ""}
                  {q.marks && testAddMarks && (
                    <span className="text-xs text-slate-400 font-normal ml-1">[{q.marks} mark{q.marks !== 1 ? "s" : ""}]</span>
                  )}
                </p>
                {ans.answer_type === "MCQ" ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-slate-500">Answer:</span>
                    <span className={`font-medium ${ans.is_correct ? "text-emerald-700" : "text-red-600"}`}>
                      {ans.selected_option || "Not answered"}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${ans.is_correct ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                      {ans.is_correct ? `+${ans.marks_awarded}` : "0"}
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 flex-wrap">
                      <span className="text-slate-500 flex-shrink-0">Response:</span>
                      <span className="text-slate-800 italic">{ans.typed_answer || "(no response)"}</span>
                    </div>
                    {q.correct_answer && (
                      <p className="text-xs text-slate-400">Expected: {q.correct_answer}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-600 font-semibold">Award marks:</label>
                      <input
                        type="number"
                        min={0}
                        max={q.marks || 10}
                        value={titaMarks[ans.question_number] ?? (ans.marks_awarded ?? "")}
                        onChange={(e) =>
                          setTitaMarks((prev) => ({
                            ...prev,
                            [ans.question_number]: parseInt(e.target.value, 10) || 0,
                          }))
                        }
                        className="w-16 border border-slate-200 rounded-lg p-1.5 text-xs text-center focus:outline-none focus:ring-2 focus:ring-sky-300"
                        data-testid={`tita-marks-${sub.learner_id}-${ans.question_number}`}
                      />
                      <span className="text-xs text-slate-400">/ {q.marks || "?"}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {hasTita && Object.keys(titaMarks).length > 0 && (
            <Button
              size="sm"
              onClick={handleSaveTita}
              className="bg-sky-600 hover:bg-sky-700 text-xs"
              data-testid={`save-tita-marks-${sub.learner_id}`}
            >
              Save marks
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function TestResultPanel({ test, onBack }) {
  const [detail, setDetail] = useState(null);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    getTestResultDetail(test.test_id).then(setDetail).catch(() => {});
  }, [test.test_id]);

  const handleAwardMarks = async (learnerId, marks) => {
    await awardTitaMarks(test.test_id, learnerId, marks);
    const updated = await getTestResultDetail(test.test_id);
    setDetail(updated);
  };

  const handlePublishScores = async () => {
    setPublishing(true);
    try {
      await publishTestScores(test.test_id);
      const updated = await getTestResultDetail(test.test_id);
      setDetail(updated);
      toast.success("Scores published to students!");
    } catch {
      toast.error("Failed to publish scores");
    } finally {
      setPublishing(false);
    }
  };

  const allScoresReleased = detail?.submissions?.length > 0 && detail?.submissions?.every((s) => s.scores_released);
  const submissionRate = detail ? Math.round((detail.submissions?.length / Math.max(detail.total_assigned, 1)) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="test-result-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition"
          data-testid="back-to-overview-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to overview
        </button>
        {detail && detail.submissions?.length > 0 && !allScoresReleased && (
          <Button
            onClick={handlePublishScores}
            disabled={publishing}
            className="bg-emerald-600 hover:bg-emerald-700 text-sm"
            data-testid="publish-scores-btn"
          >
            {publishing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Publish Scores to Students
          </Button>
        )}
        {allScoresReleased && (
          <span className="text-sm text-emerald-700 font-semibold flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4" /> All scores published
          </span>
        )}
      </div>

      {/* Test header stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Assigned", value: detail?.total_assigned ?? "—", icon: Users, color: "bg-slate-50 text-slate-700" },
          { label: "Submitted", value: detail?.submissions?.length ?? "—", icon: ClipboardList, color: "bg-sky-50 text-sky-700" },
          { label: "Submission rate", value: detail ? `${submissionRate}%` : "—", icon: TrendingUp, color: "bg-violet-50 text-violet-700" },
          { label: "Avg score", value: test.avg_score !== null && test.avg_score !== undefined ? `${test.avg_score}${test.total_marks ? `/${test.total_marks}` : ""}` : "—", icon: Star, color: "bg-amber-50 text-amber-700" },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className={`rounded-xl p-3 border-0 ${color}`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon className="w-3.5 h-3.5 opacity-70" />
              <span className="text-xs font-semibold opacity-70">{label}</span>
            </div>
            <p className="text-xl font-bold">{value}</p>
          </Card>
        ))}
      </div>

      <Card className="rounded-xl p-4 bg-white border border-slate-200">
        <div className="flex items-center gap-2">
          <h3 className="font-teacher font-bold text-slate-900 text-sm">
            {test.subject} — {test.chapter}
          </h3>
          {test.test_type === "FM" && (
            <span className="inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 flex-shrink-0">
              <Layers className="w-3 h-3" /> FM
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
          <span>Grade {test.grade}</span>
          <span>·</span>
          <span>{test.mode === "manual" ? "Manual" : "Auto-generated"}</span>
          {test.test_type === "FM" && test.fm_difficulty && <span>· {test.fm_difficulty}</span>}
          {test.add_marks && test.total_marks && <span>· {test.total_marks} mark{test.total_marks === 1 ? "" : "s"} total</span>}
          {test.learner_name && <span className="text-violet-600 font-semibold">· Individual: {test.learner_name}</span>}
        </div>
      </Card>

      {!detail ? (
        <div className="text-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading submissions...
        </div>
      ) : detail.submissions?.length === 0 ? (
        <Card className="rounded-xl p-8 text-center border border-slate-200">
          <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">No submissions yet</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {detail.submissions.map((sub) => (
            <SubmissionRow
              key={sub.learner_id}
              sub={sub}
              testAddMarks={detail.add_marks}
              testQuestions={detail.questions}
              onAwardMarks={(learnerId, marks) => handleAwardMarks(learnerId, marks)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Summary stat card ─────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon: Icon, color }) {
  return (
    <Card className={`rounded-2xl p-4 border-0 ${color}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold opacity-60 uppercase tracking-wide mb-1">{label}</p>
          <p className="text-3xl font-bold">{value}</p>
          {sub && <p className="text-xs mt-1 opacity-70">{sub}</p>}
        </div>
        <Icon className="w-5 h-5 opacity-30" />
      </div>
    </Card>
  );
}

// ── Main overview component ───────────────────────────────────────────────────

export default function TeacherTestResults({ learnerId = null, initialGrade = null }) {
  const { teacherId } = useRole();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);

  // Filters and sort
  const [filterGrade, setFilterGrade] = useState(() => initialGrade ? String(initialGrade) : "all");
  const [filterSubject, setFilterSubject] = useState("all");
  const [filterMode, setFilterMode] = useState("all");

  // Sync grade filter when parent grade context changes (tab switch while in a grade)
  useEffect(() => {
    setFilterGrade(initialGrade ? String(initialGrade) : "all");
  }, [initialGrade]);

  // Delete mode state
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleSelection = (testId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(testId)) next.delete(testId); else next.add(testId);
      return next;
    });
  };

  const handleTrashClick = (e, testId) => {
    e.stopPropagation();
    setDeleteMode(true);
    setSelectedIds(new Set([testId]));
  };

  const exitDeleteMode = () => {
    setDeleteMode(false);
    setSelectedIds(new Set());
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await bulkSoftDeleteTests([...selectedIds]);
      setTests(prev => prev.filter(t => !selectedIds.has(t.test_id)));
      toast.success(`${selectedIds.size} test${selectedIds.size !== 1 ? "s" : ""} deleted`);
      exitDeleteMode();
      setShowDeleteConfirm(false);
    } catch {
      toast.error("Failed to delete tests");
    } finally {
      setIsDeleting(false);
    }
  };

  // Deleted tests state
  const [deletedTests, setDeletedTests] = useState(null); // null = not yet fetched
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [hardDeleteTargetId, setHardDeleteTargetId] = useState(null);
  const [isHardDeleting, setIsHardDeleting] = useState(false);
  const [isRestoring, setIsRestoringId] = useState(null);

  // Lazy-load deleted tests when filter switches to "deleted"
  useEffect(() => {
    if (filterMode === "deleted" && deletedTests === null && teacherId) {
      setLoadingDeleted(true);
      listDeletedTests(teacherId)
        .then(data => setDeletedTests(data.tests || []))
        .catch(() => setDeletedTests([]))
        .finally(() => setLoadingDeleted(false));
    }
  }, [filterMode, teacherId, deletedTests]);

  const handleRestore = async (testId) => {
    setIsRestoringId(testId);
    try {
      await restoreTest(testId);
      setDeletedTests(prev => prev.filter(t => t.test_id !== testId));
      toast.success("Test restored — students can see it again");
    } catch {
      toast.error("Failed to restore test");
    } finally {
      setIsRestoringId(null);
    }
  };

  const handleHardDeleteConfirm = async () => {
    if (!hardDeleteTargetId) return;
    setIsHardDeleting(true);
    try {
      await hardDeleteTest(hardDeleteTargetId);
      setDeletedTests(prev => prev.filter(t => t.test_id !== hardDeleteTargetId));
      toast.success("Test permanently deleted");
      setHardDeleteTargetId(null);
    } catch {
      toast.error("Failed to permanently delete test");
    } finally {
      setIsHardDeleting(false);
    }
  };
  const [sortKey, setSortKey] = useState("published_at"); // "published_at" | "submission_rate" | "avg_score"
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"

  useEffect(() => {
    if (!teacherId) return;
    listTeacherTestResults(teacherId)
      .then((data) => {
        const list = data.tests || [];
        setTests(learnerId ? list.filter((t) => t.learner_id === learnerId || !t.learner_id) : list);
      })
      .catch(() => setTests([]))
      .finally(() => setLoading(false));
  }, [teacherId, learnerId]);

  // Derived filter options
  const grades = useMemo(() => [...new Set(tests.map((t) => t.grade).filter(Boolean))].sort((a, b) => a - b), [tests]);
  const subjects = useMemo(() => [...new Set(tests.map((t) => t.subject).filter(Boolean))].sort(), [tests]);

  // Summary stats — dynamic based on active/deleted mode
  const stats = useMemo(() => {
    const source = filterMode === "deleted" ? (deletedTests || []) : tests;
    const totalTests = source.length;
    const totalAssigned = source.reduce((s, t) => s + (t.total_assigned || 0), 0);
    const totalSubmitted = source.reduce((s, t) => s + (t.submissions_count || 0), 0);
    const avgRate = totalAssigned > 0 ? Math.round((totalSubmitted / totalAssigned) * 100) : 0;
    const pendingScores = source.filter((t) => t.submissions_count > 0 && t.scores_released_count < t.submissions_count).length;
    return { totalTests, totalSubmitted, avgRate, pendingScores };
  }, [tests, deletedTests, filterMode]);

  // Filtered + sorted list
  const filtered = useMemo(() => {
    let list = [...tests];
    if (filterGrade !== "all") list = list.filter((t) => String(t.grade) === filterGrade);
    if (filterSubject !== "all") list = list.filter((t) => t.subject === filterSubject);
    if (filterMode !== "all") list = list.filter((t) => t.mode === filterMode);

    list.sort((a, b) => {
      let av, bv;
      if (sortKey === "submission_rate") {
        av = a.total_assigned > 0 ? a.submissions_count / a.total_assigned : 0;
        bv = b.total_assigned > 0 ? b.submissions_count / b.total_assigned : 0;
      } else if (sortKey === "avg_score") {
        av = a.avg_score ?? -1;
        bv = b.avg_score ?? -1;
      } else {
        av = a.published_at || "";
        bv = b.published_at || "";
      }
      return sortDir === "desc" ? (bv > av ? 1 : -1) : (av > bv ? 1 : -1);
    });
    return list;
  }, [tests, filterGrade, filterSubject, filterMode, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  if (selectedTest) {
    return <TestResultPanel test={selectedTest} onBack={() => setSelectedTest(null)} />;
  }

  if (loading) {
    return (
      <div className="text-center py-16 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Loading test results...
      </div>
    );
  }

  if (tests.length === 0) {
    return (
      <Card className="rounded-2xl p-12 text-center border border-slate-200 bg-white" data-testid="no-test-results">
        <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-600 font-medium">No published tests yet</p>
        <p className="text-slate-400 text-sm mt-1">Tests you publish appear here with class-wide analytics.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="teacher-test-results-overview">

      {/* Summary stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label={filterMode === "deleted" ? "Tests Deleted" : "Tests Published"}
          value={stats.totalTests}
          icon={filterMode === "deleted" ? Trash2 : ClipboardList}
          color={filterMode === "deleted" ? "bg-slate-700 text-white" : "bg-slate-900 text-white"}
        />
        <StatCard label="Total Submissions" value={stats.totalSubmitted} icon={Users} color="bg-sky-50 text-sky-800" />
        <StatCard label="Avg Submission Rate" value={`${stats.avgRate}%`} sub="across all tests" icon={TrendingUp} color="bg-violet-50 text-violet-800" />
        <StatCard
          label="Scores Pending"
          value={stats.pendingScores}
          sub="test(s) not yet published"
          icon={AlertCircle}
          color={stats.pendingScores > 0 ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}
        />
      </div>

      {/* Filter + sort bar */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="filter-bar">
        <div className="flex items-center gap-1 text-xs text-slate-500 font-semibold">
          <Filter className="w-3.5 h-3.5" /> Filter:
        </div>

        {grades.length > 1 && (
          <select
            value={filterGrade}
            onChange={(e) => setFilterGrade(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-sky-300"
            data-testid="filter-grade"
          >
            <option value="all">All Grades</option>
            {grades.map((g) => <option key={g} value={String(g)}>Grade {g}</option>)}
          </select>
        )}

        {subjects.length > 1 && (
          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-sky-300"
            data-testid="filter-subject"
          >
            <option value="all">All Subjects</option>
            {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        <select
          value={filterMode}
          onChange={(e) => setFilterMode(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-sky-300"
          data-testid="filter-mode"
        >
          <option value="all">All Modes</option>
          <option value="auto">Auto-Generated</option>
          <option value="manual">Manual</option>
          <option value="deleted">Deleted Tests</option>
        </select>

        <div className="ml-auto flex items-center gap-1 text-xs text-slate-500 font-semibold">
          <ArrowUpDown className="w-3.5 h-3.5" /> Sort:
        </div>
        {[
          { key: "published_at", label: "Date" },
          { key: "submission_rate", label: "Submission %" },
          { key: "avg_score", label: "Avg Score" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => toggleSort(key)}
            className={`text-xs px-2.5 py-1.5 rounded-lg border transition ${
              sortKey === key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
            data-testid={`sort-${key}`}
          >
            {label} {sortKey === key && (sortDir === "desc" ? "↓" : "↑")}
          </button>
        ))}
      </div>

      {/* ── Deleted Tests view ──────────────────────────────────────── */}
      {filterMode === "deleted" && (
        <div className="space-y-3" data-testid="deleted-tests-list">
          {loadingDeleted ? (
            <div className="text-center py-10 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading deleted tests…
            </div>
          ) : !deletedTests || deletedTests.length === 0 ? (
            <Card className="rounded-2xl p-10 text-center border border-dashed border-slate-200 bg-slate-50">
              <CheckCircle2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 font-medium">No deleted tests</p>
              <p className="text-slate-400 text-sm mt-1">Tests you delete will appear here for recovery.</p>
            </Card>
          ) : (
            <>
              <p className="text-xs text-slate-500 px-1">
                These tests are hidden from students. Restore them to re-publish, or delete permanently to remove all records.
              </p>
              {deletedTests.map((test) => (
                <DeletedTestCard
                  key={test.test_id}
                  test={test}
                  isRestoring={isRestoring === test.test_id}
                  onRestore={() => handleRestore(test.test_id)}
                  onHardDelete={() => setHardDeleteTargetId(test.test_id)}
                />
              ))}
            </>
          )}
        </div>
      )}

      {/* ── Active Tests view ───────────────────────────────────────── */}
      {filterMode !== "deleted" && (<>
      {/* Test cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">No tests match the current filters.</div>
      ) : (
        <div className="space-y-3" data-testid="test-results-list">

          {/* Delete-mode action bar */}
          {deleteMode && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="delete-mode-bar">
              <CheckSquare className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm font-semibold text-red-800">
                {selectedIds.size} test{selectedIds.size !== 1 ? "s" : ""} selected
              </span>
              <button
                onClick={() => setSelectedIds(new Set(filtered.map(t => t.test_id)))}
                className="text-xs text-red-600 hover:underline"
                data-testid="select-all-btn"
              >
                Select all
              </button>
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={exitDeleteMode}
                  className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition flex items-center gap-1"
                  data-testid="cancel-delete-btn"
                >
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={selectedIds.size === 0}
                  className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 transition font-semibold"
                  data-testid="delete-selected-btn"
                >
                  Delete Selected ({selectedIds.size})
                </button>
              </div>
            </div>
          )}

          {filtered.map((test) => {
            const subRate = test.total_assigned > 0
              ? Math.round((test.submissions_count / test.total_assigned) * 100)
              : 0;
            const allReleased = test.submissions_count > 0 && test.scores_released_count === test.submissions_count;
            const pendingRelease = test.submissions_count > 0 && test.scores_released_count < test.submissions_count;
            const isSelected = selectedIds.has(test.test_id);

            return (
              <Card
                key={test.test_id}
                className={`rounded-2xl border bg-white transition cursor-pointer overflow-hidden relative ${
                  deleteMode && isSelected
                    ? "border-red-400 ring-2 ring-red-200 shadow-md"
                    : "border-slate-200 hover:shadow-md"
                }`}
                onClick={() => deleteMode ? toggleSelection(test.test_id) : setSelectedTest(test)}
                data-testid={`test-result-card-${test.test_id}`}
              >
                {/* Checkbox overlay in delete mode */}
                {deleteMode && (
                  <div
                    className="absolute top-3 left-3 z-10"
                    onClick={e => { e.stopPropagation(); toggleSelection(test.test_id); }}
                  >
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition ${
                      isSelected ? "bg-red-600 border-red-600" : "bg-white border-slate-300"
                    }`}>
                      {isSelected && <X className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                )}

                <div className={`p-4 ${deleteMode ? "pl-10" : ""}`}>
                  {/* Card header */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        test.test_type === "FM" ? "bg-fuchsia-100 text-fuchsia-600" :
                        test.mode === "manual" ? "bg-violet-100 text-violet-600" : "bg-sky-100 text-sky-600"
                      }`}>
                        {test.test_type === "FM" ? <Layers className="w-4 h-4" /> : test.mode === "manual" ? <PenLine className="w-4 h-4" /> : <ClipboardList className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        {/* Subject + FM badge */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-semibold text-slate-900">{test.subject}</p>
                          {test.test_type === "FM" && (
                            <span className="inline-flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 flex-shrink-0" data-testid={`fm-result-badge-${test.test_id}`}>
                              <Layers className="w-2.5 h-2.5" /> FM
                            </span>
                          )}
                        </div>

                        {/* Chapters line */}
                        <ChaptersLine test={test} />

                        {/* Meta pills row */}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1.5 text-xs text-slate-500">
                          <span>Grade {test.grade}</span>
                          <span>·</span>
                          <span>{test.mode === "manual" ? "Manual" : "Auto"}</span>
                          {/* Difficulty */}
                          {test.fm_difficulty && (
                            <><span>·</span><span className="text-fuchsia-700 font-medium">{test.fm_difficulty}</span></>
                          )}
                          {/* Marks · Questions */}
                          {test.add_marks && test.total_marks ? (
                            <><span>·</span><span className="font-medium text-slate-600">{test.total_marks} mark{test.total_marks === 1 ? "" : "s"} · {test.total_questions} question{test.total_questions === 1 ? "" : "s"}</span></>
                          ) : test.total_questions > 0 ? (
                            <><span>·</span><span className="font-medium text-slate-600">{test.total_questions} question{test.total_questions === 1 ? "" : "s"}</span></>
                          ) : null}
                          {/* Published to */}
                          <PublishedToPill test={test} />
                        </div>
                      </div>
                    </div>

                    <div className="flex-shrink-0 flex flex-col items-end gap-1">
                      {/* Trash icon — always visible, enters delete mode */}
                      {!deleteMode && (
                        <button
                          onClick={(e) => handleTrashClick(e, test.test_id)}
                          className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition"
                          title="Delete test"
                          data-testid={`trash-btn-${test.test_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {allReleased ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 font-semibold px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="w-3 h-3" /> Scores released
                        </span>
                      ) : pendingRelease ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full">
                          <Clock className="w-3 h-3" /> Scores pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-500 font-semibold px-2 py-0.5 rounded-full">
                          No submissions
                        </span>
                      )}
                      {test.published_at && (
                        <p className="text-xs text-slate-400">{new Date(test.published_at).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>

                  {/* Metrics row */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {/* Submission rate with progress bar */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-500">Submitted</span>
                        <span className="text-xs font-bold text-slate-700">
                          {test.submissions_count}/{test.total_assigned}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-sky-500 transition-all"
                          style={{ width: `${subRate}%` }}
                        />
                      </div>
                      <p className="text-xs text-sky-700 font-semibold mt-0.5">{subRate}%</p>
                    </div>

                    {/* Avg score */}
                    <div>
                      <span className="text-xs text-slate-500 block mb-1">Avg Score</span>
                      {test.avg_score !== null && test.avg_score !== undefined ? (
                        <div>
                          <span className="text-sm font-bold text-amber-700">
                            {test.avg_score}
                            {test.total_marks ? (
                              <span className="text-xs text-slate-400 font-normal">/{test.total_marks}</span>
                            ) : null}
                          </span>
                          {test.total_marks && (
                            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1">
                              <div
                                className="h-full rounded-full bg-amber-400 transition-all"
                                style={{ width: `${Math.round((test.avg_score / test.total_marks) * 100)}%` }}
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">
                          {test.add_marks ? "No data yet" : "Marks not enabled"}
                        </span>
                      )}
                    </div>

                    {/* Scores released */}
                    <div>
                      <span className="text-xs text-slate-500 block mb-1">Scores Released</span>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-0.5">
                        <div
                          className="h-full rounded-full bg-emerald-500 transition-all"
                          style={{ width: test.submissions_count > 0 ? `${Math.round((test.scores_released_count / test.submissions_count) * 100)}%` : "0%" }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-emerald-700">
                        {test.scores_released_count}/{test.submissions_count}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      </>)}

      {/* Hard delete confirmation dialog */}
      <AlertDialog open={!!hardDeleteTargetId} onOpenChange={(open) => !open && setHardDeleteTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Flame className="w-5 h-5" /> Permanently delete this test?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-red-700">This cannot be undone.</span> The test, all question data,
              and submission records will be erased from the database permanently.
              Students will never be able to access or view this test again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isHardDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHardDeleteConfirm}
              disabled={isHardDeleting}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="confirm-hard-delete-action"
            >
              {isHardDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-1 inline" /> : null}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} test{selectedIds.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedIds.size === 1
                ? "This test will be hidden from students immediately. Submission results are preserved and can be restored later."
                : `These ${selectedIds.size} tests will be hidden from students immediately. Results are preserved and can be restored later.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-action"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-1 inline" /> : null}
              Delete {selectedIds.size > 1 ? `All ${selectedIds.size}` : "Test"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
